import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon, DocumentIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

interface FileUploadProps {
  onFileUpload: (file: File) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileUpload }) => {
  const [dragOver, setDragOver] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setDragOver(false);
    
    if (rejectedFiles.length > 0) {
      const error = rejectedFiles[0].errors[0];
      if (error.code === 'file-too-large') {
        toast.error('Le fichier est trop volumineux (max 10MB)');
      } else if (error.code === 'file-invalid-type') {
        toast.error('Format de fichier non supporté. Utilisez .xlsx ou .xls');
      } else {
        toast.error('Erreur lors du téléchargement du fichier');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      onFileUpload(file);
    }
  }, [onFileUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter: () => setDragOver(true),
    onDragLeave: () => setDragOver(false),
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false
  });

  return (
    <div className="max-w-2xl mx-auto">
      <div
        {...getRootProps()}
        className={`upload-zone cursor-pointer ${
          isDragActive || dragOver ? 'drag-over' : ''
        }`}
      >
        <input {...getInputProps()} />
        
        <div className="flex flex-col items-center space-y-4">
          <div className="p-4 bg-blue-100 rounded-full">
            <CloudArrowUpIcon className="h-12 w-12 text-blue-600" />
          </div>
          
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {isDragActive || dragOver 
                ? 'Déposez votre fichier Excel ici' 
                : 'Uploadez votre fichier Excel'}
            </h3>
            <p className="text-gray-600 mb-4">
              Glissez-déposez votre fichier ou cliquez pour parcourir
            </p>
            <p className="text-sm text-gray-500">
              Formats supportés: .xlsx, .xls (max 10MB)
            </p>
          </div>
          
          <button
            type="button"
            className="btn-primary space-x-2"
          >
            <DocumentIcon className="h-5 w-5" />
            <span>Choisir un fichier</span>
          </button>
        </div>
      </div>
      
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800">Structure de fichier recommandée</h4>
            <ul className="mt-2 text-sm text-yellow-700 space-y-1">
              <li>• Première ligne: En-têtes (noms des colonnes)</li>
              <li>• Colonnes avec dates ou périodes (Janvier, Février, S1, S2, etc.)</li>
              <li>• Colonnes avec objectifs ou budgets</li>
              <li>• Données numériques pour l'analyse financière</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload; 